import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
    encodeHandoffHold,
    HANDOFF_HOLD_STORAGE_KEY,
    isHandoffHoldFor,
    isStaleHandoffHold,
    loginSessionKeyOf,
} from './handoff.logic';

/**
 * What the onboarding handoff into My Day holds (plan §3.2 "Handoff", D17).
 *
 * P0-13-b places this shape and the hooks that read it: My Day's grid (mine.component: the "You're in" emitters, the
 * `vf-onboarding-new-row` row class, the first-email coach-mark) and the overlay host in app.component.html. B-11 drives
 * it (sequence, reduced motion, highlight duration, a hold keyed to the login session) without editing either file.
 */
export interface OnboardingHandoffState {
    /**
     * The handoff is running: the cards align, the rail fades and the dark stage crossfades into My Day, with the new
     * tasks highlighted. While true, My Day's first-email coach-mark waits (it would cover the highlight).
     */
    readonly active: boolean;
    /** The tasks onboarding created, highlighted in My Day while listed (OB-H-02). Positive ids, no duplicates. */
    readonly highlightTaskIds: readonly number[];
    /** The "You're in / Pin Trovve to Teams" modal waits for the next session (OB-H-03). Set by `begin`. */
    readonly holdUserGuide: boolean;
}

/** No handoff: nothing highlighted, nothing held. */
export const IDLE_ONBOARDING_HANDOFF: OnboardingHandoffState = Object.freeze({
    active: false,
    highlightTaskIds: Object.freeze([]) as readonly number[],
    holdUserGuide: false,
});

/**
 * No handoff running, but this sign-in finished onboarding earlier (before an F5 or in another tab): the "You're in"
 * modal is still held (B-11, OB-H-03).
 */
export const HELD_ONBOARDING_HANDOFF: OnboardingHandoffState = Object.freeze({
    active: false,
    highlightTaskIds: IDLE_ONBOARDING_HANDOFF.highlightTaskIds,
    holdUserGuide: true,
});

/**
 * Where the "You're in" hold is kept between page loads of one sign-in (B-11, OB-H-03). Each member may throw (storage
 * blocked in a private window or a locked-down Teams webview); the service then keeps the hold in memory only.
 */
export interface OnboardingHandoffHoldStorage {
    /** The key of the current sign-in (`loginSessionKeyOf` its access token, never the token), or null if unknown. */
    readonly loginSessionKey: () => string | null;
    readonly getItem: (key: string) => string | null;
    readonly setItem: (key: string, value: string) => void;
    readonly removeItem: (key: string) => void;
}

/**
 * The browser's hold storage: localStorage (every tab and every reload of the sign-in share it), keyed by the sign-in's
 * access token (`abp.auth.getToken()`, the token AbpHttpInterceptor sends). Null without a window.
 */
export function browserHandoffHoldStorage(): OnboardingHandoffHoldStorage | null {
    if (typeof window === 'undefined') {
        return null;
    }
    return {
        loginSessionKey: (): string | null => loginSessionKeyOf(readSignInToken()),
        getItem: (key: string): string | null => window.localStorage.getItem(key),
        setItem: (key: string, value: string): void => window.localStorage.setItem(key, value),
        removeItem: (key: string): void => window.localStorage.removeItem(key),
    };
}

/** The hold storage the service uses. Root default: the browser's; a spec provides a stand-in. */
export const ONBOARDING_HANDOFF_HOLD_STORAGE = new InjectionToken<OnboardingHandoffHoldStorage | null>(
    'ONBOARDING_HANDOFF_HOLD_STORAGE',
    {
        providedIn: 'root',
        factory: browserHandoffHoldStorage,
    }
);

/** The sign-in's access token, or null (no ABP script, signed out, or the read failed). */
function readSignInToken(): string | null {
    let token: string | null = null;

    try {
        token =
            typeof abp !== 'undefined' && abp.auth && typeof abp.auth.getToken === 'function'
                ? abp.auth.getToken()
                : null;
    } catch {
        token = null;
    }
    return typeof token === 'string' && token.length > 0 ? token : null;
}

/**
 * The task ids to highlight: whole positive numbers only, in their first order, each once. Anything else (0, negative,
 * fractional, NaN, missing) is dropped. Always a new frozen array, so the caller's array is never kept or changed.
 */
export function normalizeHighlightTaskIds(ids: readonly number[] | null | undefined): readonly number[] {
    const seen: Set<number> = new Set<number>();
    const result: number[] = [];

    (ids || []).forEach((id: number) => {
        if (typeof id === 'number' && Number.isInteger(id) && id > 0 && !seen.has(id)) {
            seen.add(id);
            result.push(id);
        }
    });
    return Object.freeze(result);
}

/** Whether two id lists hold the same ids in the same order. */
export function sameTaskIds(a: readonly number[], b: readonly number[]): boolean {
    return a.length === b.length && a.every((id: number, index: number) => id === b[index]);
}

/** The state once the handoff begins: running, the "You're in" modal held for this session, these tasks highlighted. */
export function beginHandoff(
    state: OnboardingHandoffState,
    highlightTaskIds: readonly number[] | null | undefined
): OnboardingHandoffState {
    const ids: readonly number[] = normalizeHighlightTaskIds(highlightTaskIds);

    if (state.active && state.holdUserGuide && sameTaskIds(state.highlightTaskIds, ids)) {
        return state;
    }
    return Object.freeze({ active: true, highlightTaskIds: ids, holdUserGuide: true });
}

/** The state with no task highlighted (the handoff itself may still be running; the hold stays). */
export function clearHandoffHighlights(state: OnboardingHandoffState): OnboardingHandoffState {
    if (state.highlightTaskIds.length === 0) {
        return state;
    }
    return Object.freeze({ ...state, highlightTaskIds: IDLE_ONBOARDING_HANDOFF.highlightTaskIds });
}

/**
 * FX-7 (OB-H-02): whether any highlighted task has a row node in My Day's grid (`hasNode` answers per task id, and is
 * asked only until one answers true).
 */
export function hasDrawnHighlight(ids: readonly number[], hasNode: (taskId: number) => boolean): boolean {
    return ids.some((taskId: number) => hasNode(taskId) === true);
}

/** The state once the handoff is over: not running, nothing highlighted. The hold stays for the rest of this session. */
export function endHandoff(state: OnboardingHandoffState): OnboardingHandoffState {
    if (!state.active && state.highlightTaskIds.length === 0) {
        return state;
    }
    return Object.freeze({
        active: false,
        highlightTaskIds: IDLE_ONBOARDING_HANDOFF.highlightTaskIds,
        holdUserGuide: state.holdUserGuide,
    });
}

/**
 * `OnboardingHandoffService` — the one handoff state between the end of onboarding and My Day (P0-13-b; B-11 fills it).
 *
 * Readers (placed by P0-13-b, never edited by B-11):
 *  - mine.component checks `holdUserGuideThisSession` at each of its three `openUserGuideModal` emitters, so the
 *    "You're in / Pin Trovve to Teams" modal is not shown right after onboarding (OB-H-03);
 *  - mine.component's `vf-onboarding-new-row` row class reads `isHighlighted`, and the grid redraws on
 *    `highlightTaskIds$` (OB-H-02);
 *  - mine.component skips the first-email coach-mark while `isActive`;
 *  - the `vf-onboarding-handoff-overlay` host in app.component.html runs the sequence while `active$` is true (it
 *    spans the route change) and calls `end` when the highlight is over (handoff.logic.ts `runHandoff`);
 *  - FX-7 (OB-H-02): the overlay starts the highlight's time once `highlightShown$` says My Day has drawn a highlighted
 *    task's row: mine.component calls `markHighlightShownWhenDrawn` when the highlighted ids change and when its grid's
 *    rows change. The flag replays (the overlay reads it after the crossfade) and resets on `begin` and `end`; with
 *    nothing to highlight it is set at once (there is nothing to wait for).
 *
 * Writers: the exit that finishes onboarding (the Organize screen, B-10) calls `begin` with the created task ids after
 * the server's Complete answered and before it marks completion in memory and navigates (D-08's completion contract),
 * and `end` when that navigation failed. This service never marks completion and never navigates.
 *
 * The hold (B-11, OB-H-03) is keyed to the sign-in, not to plain sessionStorage: `begin` stores a record naming the
 * sign-in (`loginSessionKeyOf` its access token, never the token) in localStorage, and a new instance (F5, a new tab)
 * starts held when the record names the current sign-in. Signing out clears the token, so the next sign-in has another
 * key: the record no longer applies (the modal shows in that session if it was never seen) and is removed at its first
 * read. Storage that cannot be read or written leaves the hold in memory for this page only.
 *
 * Typed and providedIn 'root'; never a `DataService` channel.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingHandoffService implements OnDestroy {
    /** The whole state, current value first. */
    readonly state$: Observable<OnboardingHandoffState>;
    /** Whether the handoff is running, current value first, then each change. */
    readonly active$: Observable<boolean>;
    /** The highlighted task ids, current value first, then each change. */
    readonly highlightTaskIds$: Observable<readonly number[]>;
    /**
     * FX-7 (OB-H-02): whether the highlight is on screen (My Day drew a highlighted task's row, or there is nothing to
     * highlight), current value first (it replays), then each change. Reset by `begin` and `end`.
     */
    readonly highlightShown$: Observable<boolean>;

    private readonly stateSubject: BehaviorSubject<OnboardingHandoffState>;
    private readonly shownSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly holdStorage: OnboardingHandoffHoldStorage | null;
    private highlighted: ReadonlySet<number> = new Set<number>();

    /**
     * @param holdStorage where the hold is kept between page loads of the sign-in; without it (`new` in a spec) the
     *        hold lasts for this instance only.
     */
    constructor(
        @Optional() @Inject(ONBOARDING_HANDOFF_HOLD_STORAGE) holdStorage?: OnboardingHandoffHoldStorage | null
    ) {
        this.holdStorage = holdStorage || null;
        this.stateSubject = new BehaviorSubject<OnboardingHandoffState>(
            this.restoreHold() ? HELD_ONBOARDING_HANDOFF : IDLE_ONBOARDING_HANDOFF
        );
        this.state$ = this.stateSubject.asObservable();
        this.active$ = this.state$.pipe(
            map((state: OnboardingHandoffState) => state.active),
            distinctUntilChanged()
        );
        this.highlightTaskIds$ = this.state$.pipe(
            map((state: OnboardingHandoffState) => state.highlightTaskIds),
            distinctUntilChanged()
        );
        this.highlightShown$ = this.shownSubject.pipe(distinctUntilChanged());
    }

    /** Whether the handoff is running (the first-email coach-mark waits). */
    get isActive(): boolean {
        return this.stateSubject.value.active;
    }

    /** Whether the "You're in / Pin Trovve to Teams" modal must wait for the next session (OB-H-03). */
    get holdUserGuideThisSession(): boolean {
        return this.stateSubject.value.holdUserGuide;
    }

    /** The highlighted task ids (frozen). */
    get highlightTaskIds(): readonly number[] {
        return this.stateSubject.value.highlightTaskIds;
    }

    /** Whether a task is highlighted right now (the grid's row class rule; a constant-time lookup). */
    isHighlighted(taskId: number | null | undefined): boolean {
        return typeof taskId === 'number' && this.highlighted.has(taskId);
    }

    /** FX-7: whether the highlight is on screen (see `highlightShown$`). */
    get isHighlightShown(): boolean {
        return this.shownSubject.value;
    }

    /**
     * The handoff begins: it is running, the "You're in" modal is held for this session, and these tasks (the ones
     * onboarding created) are highlighted. Called before the navigation into My Day so the first render already holds.
     * The hold is also stored for this sign-in, so it survives F5 and new tabs (OB-H-03). FX-7: the highlight is not
     * on screen yet (My Day has not drawn the rows), unless there is nothing to highlight.
     */
    begin(highlightTaskIds?: readonly number[] | null): void {
        this.set(beginHandoff(this.stateSubject.value, highlightTaskIds));
        this.setShown(this.stateSubject.value.highlightTaskIds.length === 0);
        this.storeHold();
    }

    /** Stops highlighting (the handoff may still be running; nothing is left to wait for, FX-7). */
    clearHighlights(): void {
        this.set(clearHandoffHighlights(this.stateSubject.value));
        if (this.stateSubject.value.active) {
            this.setShown(true);
        }
    }

    /**
     * FX-7 (OB-H-02): My Day has drawn a highlighted task's row, so the highlight's time starts (the overlay reads
     * `highlightShown$`). Ignored when no handoff runs.
     */
    markHighlightShown(): void {
        if (this.stateSubject.value.active) {
            this.setShown(true);
        }
    }

    /**
     * FX-7 (OB-H-02): My Day's grid calls this when the highlighted ids or its rows change: once any highlighted task has
     * a row node (`hasNode`), the highlight is marked shown. It asks `hasNode` only while a handoff runs and the
     * highlight is not marked yet, so the grid pays nothing at any other time.
     */
    markHighlightShownWhenDrawn(hasNode: (taskId: number) => boolean): void {
        const state: OnboardingHandoffState = this.stateSubject.value;

        if (!state.active || this.shownSubject.value || !hasDrawnHighlight(state.highlightTaskIds, hasNode)) {
            return;
        }
        this.setShown(true);
    }

    /** The handoff is over: nothing runs or is highlighted; the modal stays held for the rest of this session. */
    end(): void {
        this.set(endHandoff(this.stateSubject.value));
        this.setShown(false);
    }

    ngOnDestroy(): void {
        this.stateSubject.complete();
        this.shownSubject.complete();
    }

    /** Publishes a new state (an unchanged state publishes nothing). */
    private set(next: OnboardingHandoffState): void {
        if (next === this.stateSubject.value) {
            return;
        }
        this.highlighted = new Set<number>(next.highlightTaskIds);
        this.stateSubject.next(next);
    }

    /** Publishes the FX-7 highlight flag (an unchanged value publishes nothing; a completed subject ignores it). */
    private setShown(shown: boolean): void {
        if (this.shownSubject.value === shown) {
            return;
        }
        this.shownSubject.next(shown);
    }

    /**
     * Whether this sign-in already holds the "You're in" modal (a stored record naming it). A record left by an earlier
     * sign-in is removed. Never throws: storage that fails counts as no hold.
     */
    private restoreHold(): boolean {
        const storage: OnboardingHandoffHoldStorage | null = this.holdStorage;
        let sessionKey: string | null = null;
        let stored: string | null = null;

        if (!storage) {
            return false;
        }
        try {
            sessionKey = storage.loginSessionKey();
            stored = storage.getItem(HANDOFF_HOLD_STORAGE_KEY);
            if (isHandoffHoldFor(stored, sessionKey)) {
                return true;
            }
            if (isStaleHandoffHold(stored, sessionKey)) {
                storage.removeItem(HANDOFF_HOLD_STORAGE_KEY);
            }
        } catch {
            // Blocked storage: no hold from an earlier page; one set now still holds for this page.
        }
        return false;
    }

    /** Stores the hold for this sign-in (nothing without a known sign-in). Never throws: the memory hold stands. */
    private storeHold(): void {
        const storage: OnboardingHandoffHoldStorage | null = this.holdStorage;
        let sessionKey: string | null = null;

        if (!storage || !this.stateSubject.value.holdUserGuide) {
            return;
        }
        try {
            sessionKey = storage.loginSessionKey();
            if (sessionKey) {
                storage.setItem(HANDOFF_HOLD_STORAGE_KEY, encodeHandoffHold(sessionKey));
            }
        } catch {
            // Blocked or full storage: the hold lasts for this page only.
        }
    }
}
